/**
 * Context menu for right-clicking a tab in the TabBar.
 *
 * Actions: Pin/Unpin, Open in side pane, Close.
 */
import React, { useEffect, useRef } from 'react';

import { createPortal } from 'react-dom';

import { Z } from '@shared/constants/zIndex';


import { useWorkspaceNav } from '../hooks/useWorkspaceNav';
import { useWorkspaceStore, tabsForGroup } from '../stores/useWorkspaceStore';

import type { WorkspaceTab } from '../types';

interface Props {
  tab: WorkspaceTab;
  x: number;
  y: number;
  onClose: () => void;
}

export const TabContextMenu: React.FC<Props> = ({ tab, x, y, onClose }) => {
  const togglePin = useWorkspaceStore(s => s.togglePin);
  const tabs = useWorkspaceStore(s => s.tabs);
  const { closeTab, moveToSidePane } = useWorkspaceNav();
  const ref = useRef<HTMLDivElement>(null);

  const groupTabs = tabsForGroup(tabs, tab.group);
  const isLastInGroup = groupTabs.length <= 1;
  const canMoveToSide = tab.group === 'primary' && !isLastInGroup;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="border-border bg-background fixed min-w-[160px] rounded border py-1 shadow-md"
      style={{ left: x, top: y, zIndex: Z.MODAL }}
      role="menu"
    >
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          togglePin(tab.id);
          onClose();
        }}
        className="hover:bg-accent block w-full px-3 py-1.5 text-left text-sm"
      >
        {tab.pinned ? 'Unpin tab' : 'Pin tab'}
      </button>
      {canMoveToSide && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            moveToSidePane(tab.id);
            onClose();
          }}
          className="hover:bg-accent block w-full px-3 py-1.5 text-left text-sm"
        >
          Open in side pane
        </button>
      )}
      {!tab.pinned && (
        <button
          type="button"
          role="menuitem"
          disabled={isLastInGroup && tab.group === 'primary'}
          onClick={() => {
            closeTab(tab.id);
            onClose();
          }}
          className={`block w-full px-3 py-1.5 text-left text-sm ${
            isLastInGroup && tab.group === 'primary'
              ? 'text-muted-foreground/50 cursor-not-allowed'
              : 'hover:bg-accent'
          }`}
        >
          Close tab
        </button>
      )}
    </div>,
    document.body
  );
};
